import React, { useState, useEffect } from 'react';
import { useCurrency } from '../hooks/useCurrency';

interface CurrencySelectorProps {
  amount: number;
  baseCurrency?: string;
  onCurrencyChange?: (currency: string) => void;
  onAmountChange?: (amount: number) => void;
  className?: string;
  showConvertedAmount?: boolean;
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  amount,
  baseCurrency = 'EUR',
  onCurrencyChange,
  onAmountChange,
  className = '',
  showConvertedAmount = true
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState(baseCurrency);
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  
  const { 
    rates, 
    loading, 
    error, 
    convertCurrency, 
    formatCurrency, 
    supportedCurrencies 
  } = useCurrency({ baseCurrency: selectedCurrency });

  // Convertir le montant quand la devise change
  useEffect(() => {
    const convertAmount = async () => {
      if (selectedCurrency === baseCurrency) {
        setConvertedAmount(amount);
        return;
      }

      try {
        const converted = await convertCurrency(amount, baseCurrency, selectedCurrency);
        setConvertedAmount(converted);
      } catch (err) {
        console.error('Erreur lors de la conversion:', err);
        setConvertedAmount(null);
      }
    };

    convertAmount();
  }, [amount, baseCurrency, selectedCurrency, convertCurrency]);

  const handleCurrencyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newCurrency = event.target.value;
    setSelectedCurrency(newCurrency);
    onCurrencyChange?.(newCurrency);
  };

  const getCurrencyLabel = (currency: string): string => {
    const labels: { [key: string]: string } = {
      'EUR': 'Euro (€)',
      'USD': 'Dollar US ($)',
      'TND': 'Dinar tunisien (د.ت)',
      'GBP': 'Livre sterling (£)',
      'JPY': 'Yen japonais (¥)'
    };
    return labels[currency] || currency;
  };

  const getCurrencySymbol = (currency: string): string => {
    const symbols: { [key: string]: string } = {
      'EUR': '€',
      'USD': '$',
      'TND': 'د.ت',
      'GBP': '£',
      'JPY': '¥'
    };
    return symbols[currency] || currency;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Sélecteur de devise */}
      <div className="flex items-center space-x-3">
        <label className="text-sm font-medium text-gray-700">
          Devise:
        </label>
        <select
          value={selectedCurrency}
          onChange={handleCurrencyChange}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          disabled={loading}
        >
          {supportedCurrencies.map(currency => (
            <option key={currency} value={currency}>
              {getCurrencyLabel(currency)}
            </option>
          ))}
        </select>
        
        {loading && (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
            <span className="text-xs text-gray-500">Chargement...</span>
          </div>
        )}
      </div>

      {/* Affichage du montant */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            Montant en {baseCurrency}:
          </span>
          <span className="font-medium">
            {formatCurrency(amount, baseCurrency)}
          </span>
        </div>

        {showConvertedAmount && convertedAmount !== null && selectedCurrency !== baseCurrency && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Montant en {selectedCurrency}:
            </span>
            <span className="font-semibold text-indigo-600">
              {formatCurrency(convertedAmount, selectedCurrency)}
            </span>
          </div>
        )}

        {error && (
          <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
            ⚠️ {error}
          </div>
        )}

        {/* Taux de change */}
        {rates && selectedCurrency !== baseCurrency && rates[selectedCurrency] && (
          <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
            1 {baseCurrency} = {rates[selectedCurrency].toFixed(4)} {selectedCurrency}
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencySelector; 